/**
 * Configuración centralizada de variables de entorno
 * ✅ COMPATIBILIDAD TOTAL CON NEXT.JS
 * Las variables están definidas en vite.config.ts con fallback a NEXT_PUBLIC_*
 * NO hay valores hardcodeados - todo debe configurarse en Docker
 */

const LEGACY_API_PATH_SUFFIXES = [
  '/api',
  '/api/v1',
  '/api/v1/rag',
  '/api/agent/v1',
]

function normalizeApiBaseUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim()
  try {
    const parsed = new URL(trimmed)
    const normalizedPath = parsed.pathname.replace(/\/+$/, '') || '/'
    if (LEGACY_API_PATH_SUFFIXES.includes(normalizedPath)) {
      return parsed.origin
    }
    return `${parsed.origin}${normalizedPath === '/' ? '' : normalizedPath}`
  } catch {
    return trimmed.replace(/\/$/, '')
  }
}

// Helper function to get API base URL, using current origin when accessed via ngrok or external domain
function getApiBaseUrl(envUrl: string | undefined): string {
  // If no URL configured, use a local dev fallback for standalone Vite.
  // This prevents /api/* calls from hitting Vite (:3000) and returning 404.
  if (!envUrl) {
    if (typeof window !== 'undefined') {
      const host = window.location.hostname
      const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '::1'
      const isStandaloneVite = window.location.port === '3000' || window.location.port === '5173'
      if (isLocalHost && isStandaloneVite) {
        // Misma pestaña + proxy `/api` de Vite → nginx (evita 404 y puerto fijo incorrecto).
        return ''
      }
    }
    return ''
  }

  const normalizedEnvUrl = normalizeApiBaseUrl(envUrl)

  // If we're in a browser and the configured URL is localhost but we're on a different domain,
  // use relative URLs to work with ngrok/external access
  if (typeof window !== 'undefined') {
    const currentOrigin = window.location.origin
    const currentHost = window.location.hostname
    const envUrlObj = new URL(normalizedEnvUrl)

    // If configured URL is localhost but we're on a public/non-localhost domain (e.g., ngrok),
    // use relative URLs so requests go to the same origin
    const isCurrentHostLocal =
      currentHost === 'localhost' ||
      currentHost === '127.0.0.1' ||
      currentHost === '::1'

    if (envUrlObj.hostname === 'localhost' && !isCurrentHostLocal && currentOrigin !== normalizedEnvUrl) {
      return ''
    }

    // SPA en nginx :8082 pero env sigue en :8080 → peticiones al puerto equivocado y 404 HTML.
    const isViteDevPort =
      window.location.port === '3000' ||
      window.location.port === '5173'
    if (!isViteDevPort && isCurrentHostLocal) {
      try {
        if (envUrlObj.hostname === 'localhost' || envUrlObj.hostname === '127.0.0.1') {
          const envPort = envUrlObj.port || (envUrlObj.protocol === 'https:' ? '443' : '80')
          const pagePort = window.location.port || (window.location.protocol === 'https:' ? '443' : '80')
          if (String(envPort) !== String(pagePort)) {
            return ''
          }
        }
      } catch {
        /* ignore */
      }
    }
  }

  return normalizedEnvUrl
}

// API Base URLs - Compatibles con Next.js (NEXT_PUBLIC_* fallback automático)
// Uses relative URLs when accessed via ngrok/external domain
export const API_BASE_URL = getApiBaseUrl(import.meta.env.VITE_API_BASE_URL)
export const CONVERSATIONS_API_URL = getApiBaseUrl(
  import.meta.env.VITE_CONVERSATIONS_API_URL || import.meta.env.VITE_API_BASE_URL,
)
export const RAG_API_URL = getApiBaseUrl(import.meta.env.VITE_RAG_API_URL)
export const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY || import.meta.env.OPENAI_API_KEY
export const OPENAI_MODEL = import.meta.env.VITE_OPENAI_MODEL || 'gpt-4o-mini'

// Authentication Tokens - Compatibles con Next.js (NEXT_PUBLIC_* fallback automático)
export const API_TOKEN = import.meta.env.VITE_API_TOKEN
export const CONVERSATIONS_API_TOKEN = import.meta.env.VITE_CONVERSATIONS_API_TOKEN
export const RAG_API_TOKEN = import.meta.env.VITE_RAG_API_TOKEN
export const RAG_API_STATIC_TOKEN = import.meta.env.VITE_RAG_API_STATIC_TOKEN
export const ELEVENLABS_MAX_FILE_BYTES = Number(import.meta.env.VITE_ELEVENLABS_MAX_FILE_BYTES || '20971520')

// WebSocket Configuration - Compatible con Next.js (NEXT_PUBLIC_* fallback automático)
export const WS_URL = import.meta.env.VITE_WS_URL

// Auth Token Key - Compatible con Next.js (NEXT_PUBLIC_* fallback automático)
export const AUTH_TOKEN_KEY = import.meta.env.VITE_AUTH_TOKEN_KEY

// Donde se guardan los tokens que entrega el login. Son dos porque son dos
// servicios distintos, cada uno con su token: el navegador los recibe al
// entrar y los borra al salir.
export const CONVERSATIONS_TOKEN_STORAGE_KEY = AUTH_TOKEN_KEY || 'chatbot_auth_token'
export const RAG_TOKEN_STORAGE_KEY = 'rag_auth_token'

// Quien ha entrado. No es autenticacion -eso lo dicen los tokens-: es el nombre
// que queda registrado en cada decision que se toma desde la interfaz. El
// servidor exige `por` en cada aprobacion y en cada retirada, porque una
// decision sin dueno no se puede auditar ni revertir.
export const USUARIO_STORAGE_KEY = 'muveran_usuario'

export const sanitizePublicMetaValue = (value: string | undefined): string => {
  const normalized = value?.trim() || ''
  if (!normalized) {
    return ''
  }

  return /replace_me/i.test(normalized) ? '' : normalized
}

// Meta app and Business Login configuration IDs.
export const FACEBOOK_APP_ID = sanitizePublicMetaValue(import.meta.env.VITE_FACEBOOK_APP_ID)
export const WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID =
  sanitizePublicMetaValue(import.meta.env.VITE_WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID)
/** true by default. Set VITE_WHATSAPP_USE_EMBEDDED_SIGNUP=false to force the legacy Facebook login flow. */
export const WHATSAPP_USE_EMBEDDED_SIGNUP =
  import.meta.env.VITE_WHATSAPP_USE_EMBEDDED_SIGNUP !== 'false'
export const INSTAGRAM_CONFIG_ID = sanitizePublicMetaValue(import.meta.env.VITE_INSTAGRAM_CONFIG_ID)
/**
 * Instagram App ID for the "Instagram API with Instagram Login" product. This is
 * NOT the Facebook app id: the two products are reviewed and approved
 * separately. When set, the channel connects straight through Instagram and no
 * Facebook Page is involved.
 */
export const INSTAGRAM_APP_ID = sanitizePublicMetaValue(import.meta.env.VITE_INSTAGRAM_APP_ID)
/**
 * Overrides the redirect URI sent to Instagram. Meta matches it character for
 * character against the app's allow-list and is picky about what it accepts
 * there (query strings in particular), so this is settable per environment
 * instead of being derived from the current page.
 */
export const INSTAGRAM_REDIRECT_URI = sanitizePublicMetaValue(import.meta.env.VITE_INSTAGRAM_REDIRECT_URI)
export const INSTAGRAM_LOGIN_OAUTH_SCOPES = [
  'instagram_business_basic',
  'instagram_business_manage_messages',
  'instagram_business_manage_comments',
  'instagram_business_manage_insights',
]
export const buildInstagramAuthorizeUrl = (redirectUri: string): string => {
  const params = new URLSearchParams({
    client_id: INSTAGRAM_APP_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: INSTAGRAM_LOGIN_OAUTH_SCOPES.join(','),
  })
  return `https://www.instagram.com/oauth/authorize?${params.toString()}`
}
export const FACEBOOK_ADS_LEADS_CONFIG_ID =
  sanitizePublicMetaValue(import.meta.env.VITE_FACEBOOK_ADS_LEADS_CONFIG_ID)
export const META_INTEGRATION_ENABLED = import.meta.env.VITE_META_INTEGRATION_ENABLED !== 'false'
export const VISIT_SCHEDULING_ENABLED = import.meta.env.VITE_VISIT_SCHEDULING_ENABLED !== 'false'

const DEFAULT_META_GRAPH_API_VERSION = 'v25.0'
const META_GRAPH_API_ROOT = 'https://graph.facebook.com'

export const META_GRAPH_API_VERSION =
  (import.meta.env.VITE_META_GRAPH_API_VERSION || DEFAULT_META_GRAPH_API_VERSION).trim() ||
  DEFAULT_META_GRAPH_API_VERSION

export const buildMetaGraphUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path
  return `${META_GRAPH_API_ROOT}/${META_GRAPH_API_VERSION}/${normalizedPath}`
}

// Environment Detection - desde Docker environment
export const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT
export const IS_PRODUCTION = import.meta.env.PROD || ENVIRONMENT === 'production'
export const IS_DEVELOPMENT = import.meta.env.DEV || ENVIRONMENT === 'development'

// Keep local dev usable even when shell env is missing.
const DEV_FALLBACK_CONVERSATIONS_TOKEN = 'conv_debug_token_2024_dev_secure_x7k9m'

// Token para rag-api. Primero el que entregó el login; el estático queda
// detrás y en producción ya no existe, porque no se incrusta en el bundle.
// Se lee en cada llamada a propósito: al arrancar la app todavía no hay
// sesión, y un valor capturado al importar el módulo se quedaría vacío.
export const getAuthToken = (): string => {
  return localStorage.getItem(RAG_TOKEN_STORAGE_KEY) ||
    sessionStorage.getItem(RAG_TOKEN_STORAGE_KEY) ||
    RAG_API_STATIC_TOKEN ||
    localStorage.getItem(CONVERSATIONS_TOKEN_STORAGE_KEY) ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('access_token') ||
    ''
}

// Helper function to get conversations auth token with fallbacks
export const getConversationsAuthToken = (): string => {
  // Prioritize user session token over static token for browser usage
  const token = localStorage.getItem(AUTH_TOKEN_KEY || 'chatbot_auth_token') ||
    sessionStorage.getItem(AUTH_TOKEN_KEY || 'chatbot_auth_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('auth_token') ||
    CONVERSATIONS_API_TOKEN ||
    (IS_DEVELOPMENT ? DEV_FALLBACK_CONVERSATIONS_TOKEN : '') ||
    ''
  
  // Debug logging in development
  if (typeof window !== 'undefined' && (!token || token.trim() === '')) {
    console.warn('getConversationsAuthToken: No token found', {
      hasLocalStorage: !!localStorage.getItem(AUTH_TOKEN_KEY || 'chatbot_auth_token'),
      hasSessionStorage: !!sessionStorage.getItem(AUTH_TOKEN_KEY || 'chatbot_auth_token'),
      hasAccessToken: !!localStorage.getItem('access_token'),
      hasAuthToken: !!localStorage.getItem('auth_token'),
      hasEnvToken: !!CONVERSATIONS_API_TOKEN,
      envTokenPreview: CONVERSATIONS_API_TOKEN ? `${CONVERSATIONS_API_TOKEN.substring(0, 20)}...` : 'NO ENV TOKEN'
    });
  }
  
  return token
}
